// src/components/CreateOfficerSafetyReport.js

import React, { useState, useContext, useEffect, useRef } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { db } from '../utils/firebaseConfig';
import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  doc,
  query,
  where,
  Timestamp,
  runTransaction,
  updateDoc,
  deleteDoc,
  limit,
} from 'firebase/firestore';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiCalendar,
  FiAlertTriangle,
  FiClipboard,
  FiCheckCircle,
  FiUser,
  FiArrowLeft,
  FiInfo,
  FiHelpCircle,
  FiSave,
  FiEdit,
  FiList,
  FiTrash2,
} from 'react-icons/fi';
import ConfirmationModal from '../components/ConfirmationModal';
import { createReportNotification } from '../utils/notificationUtils';

const CreateOfficerSafetyReport = () => {
  const { currentUser, role } = useContext(AuthContext);
  const navigate = useNavigate();
  const modalRef = useRef(null);
  const [showHelpText, setShowHelpText] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    date: new Date().toISOString().substr(0, 10),
    traineeId: '',
    incidentDescription: '',
    safetyConcerns: '',
  });
  const [trainees, setTrainees] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isDraftModalOpen, setIsDraftModalOpen] = useState(false);
  const [isDraftSelectorModalOpen, setIsDraftSelectorModalOpen] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [loadingDraft, setLoadingDraft] = useState(true);
  const [availableDrafts, setAvailableDrafts] = useState([]);
  const [isDeleteDraftModalOpen, setIsDeleteDraftModalOpen] = useState(false);

  // Fetch trainees on mount
  useEffect(() => {
    const fetchTrainees = async () => {
      try {
        const q = query(
          collection(db, 'users'),
          where('role', '==', 'trainee'),
          where('archived', '==', false) // Exclude archived trainees
        );
        const querySnapshot = await getDocs(q);
        const traineeList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setTrainees(traineeList);
      } catch (error) {
        console.error('Error fetching trainees:', error);
        toast.error('Failed to load trainees. Please try again.');
      }
    };

    fetchTrainees();
  }, []);

  // Check for existing draft when component mounts
  useEffect(() => {
    const checkForDraft = async () => {
      if (!currentUser) return;
      
      try {
        const draftsQuery = query(
          collection(db, 'osrDrafts'),
          where('createdBy', '==', currentUser.uid)
        );
        
        const draftSnapshot = await getDocs(draftsQuery);
        
        if (!draftSnapshot.empty) {
          // Store all available drafts
          const drafts = draftSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          setAvailableDrafts(drafts);
          
          // If there's only one draft, show the regular draft modal
          if (drafts.length === 1) {
            setDraftId(drafts[0].id);
            setIsDraftModalOpen(true);
          } 
          // If there are multiple drafts, show the draft selector modal
          else if (drafts.length > 1) {
            setIsDraftSelectorModalOpen(true);
          }
        }
      } catch (error) {
        console.error('Error checking for drafts:', error);
      } finally {
        setLoadingDraft(false);
      }
    };
    
    checkForDraft();
  }, [currentUser]);

  const loadDraft = async (draftIdToLoad, showToast = true) => {
    // Use the provided draftId or fall back to the state variable
    const idToLoad = draftIdToLoad || draftId;
    
    if (!idToLoad) {
      console.log('No draftId provided to loadDraft');
      return;
    }
    
    try {
      console.log('Loading draft with ID:', idToLoad);
      const draftDocRef = doc(db, 'osrDrafts', idToLoad);
      const draftSnapshot = await getDoc(draftDocRef);
      
      if (draftSnapshot.exists()) {
        const draftData = draftSnapshot.data();
        
        // If we're loading a specific draft ID, update the state
        if (draftIdToLoad && draftIdToLoad !== draftId) {
          setDraftId(draftIdToLoad);
        }
        
        // Convert Firestore timestamps back to string format for form inputs
        let formattedData = { ...draftData };
        
        if (draftData.date) {
          // Check if date is a Firestore Timestamp
          if (draftData.date.toDate && typeof draftData.date.toDate === 'function') {
            const date = draftData.date.toDate();
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            formattedData.date = `${year}-${month}-${day}`;
          } else if (typeof draftData.date === 'string') {
            // If it's already a string in YYYY-MM-DD format, use it directly
            formattedData.date = draftData.date;
          }
        }
        
        // Ensure all required fields are present
        const defaultFormData = {
          traineeId: formattedData.traineeId || '',
          date: formattedData.date || (() => {
            const today = new Date();
            return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
          })(),
          incidentDescription: formattedData.incidentDescription || '',
          safetyConcerns: formattedData.safetyConcerns || ''
        };
        
        setFormData(defaultFormData);
        if (showToast) {
          toast.info('Draft loaded successfully');
        }
      } else {
        console.log('Draft document does not exist');
        if (showToast) {
          toast.error('Draft not found');
        }
      }
    } catch (error) {
      console.error('Error loading draft:', error);
      if (showToast) {
        toast.error('Failed to load draft');
      }
    }
  };

  const selectDraft = async (draftId) => {
    console.log('Selecting draft with ID:', draftId);
    
    // Set the draft ID and close the modal
    setDraftId(draftId);
    setIsDraftSelectorModalOpen(false);
    
    // Load the draft with the specific ID
    await loadDraft(draftId);
    
    console.log('Draft selection complete');
  };

  const startFresh = () => {
    setDraftId(null);
    setIsDraftModalOpen(false);
    setIsDraftSelectorModalOpen(false);
    
    // Reset form data to default values
    setFormData({
      traineeId: '',
      date: (() => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      })(),
      incidentDescription: '',
      safetyConcerns: ''
    });
  };

  const handleSaveDraft = async () => {
    if (!currentUser) {
      toast.error('You must be logged in to save a draft');
      return;
    }
    
    if (!formData.traineeId) {
      toast.warning('Please select a trainee before saving a draft');
      return;
    }
    
    setSavingDraft(true);
    
    try {
      // Prepare draft data - store dates as strings to avoid conversion issues
      const draftData = {
        ...formData,
        createdBy: currentUser.uid,
        lastUpdated: Timestamp.now(),
        // Ensure date is stored as a string
        date: formData.date
      };
      
      // If we already have a draft ID, update it, otherwise create a new one
      if (draftId) {
        const draftRef = doc(db, 'osrDrafts', draftId);
        await updateDoc(draftRef, draftData);
      } else {
        const draftRef = collection(db, 'osrDrafts');
        const newDraftDoc = await addDoc(draftRef, draftData);
        setDraftId(newDraftDoc.id);
      }
      
      toast.success('Draft saved successfully');
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error('Failed to save draft');
    } finally {
      setSavingDraft(false);
    }
  };

  const handleDeleteDraft = () => {
    setIsDeleteDraftModalOpen(true);
  };

  const confirmDeleteDraft = async () => {
    if (!draftId) return;
    
    try {
      const draftRef = doc(db, 'osrDrafts', draftId);
      await deleteDoc(draftRef);
      setDraftId(null);
      setIsDeleteDraftModalOpen(false);
      toast.success('Draft deleted successfully');
    } catch (error) {
      console.error('Error deleting draft:', error);
      toast.error('Failed to delete draft');
    }
  };

  // Guard clause if user doesn't have appropriate role
  if (role !== 'admin' && role !== 'trainer') {
    return (
      <div className="p-6 flex items-center justify-center h-full">
        <div className="bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4 rounded-lg shadow-md flex items-center">
          <FiAlertTriangle className="mr-2 flex-shrink-0" size={20} />
          <span>Access Denied - You don't have permission to create Officer Safety Reports</span>
        </div>
      </div>
    );
  }

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.traineeId) {
      toast.error('Please select a trainee.');
      return;
    }

    setIsConfirmModalOpen(true);
    // No need for manual scrolling anymore as the modal is now properly positioned
  };

  const handleConfirm = async () => {
    setIsConfirmModalOpen(false);
    setSubmitting(true);
    try {
      // Parse date in local time
      const [year, month, day] = formData.date.split('-').map(Number);
      const reportDate = new Date(year, month - 1, day);

      // Fetch trainee data
      const traineeDocRef = doc(db, 'users', formData.traineeId);
      const traineeDocSnap = await getDoc(traineeDocRef);
      if (!traineeDocSnap.exists()) {
        toast.error('Selected trainee does not exist.');
        setSubmitting(false);
        return;
      }
      const traineeData = traineeDocSnap.data();
      const traineeArchived = traineeData.archived || false;

      // Fetch trainer data
      const trainerDocRef = doc(db, 'users', currentUser.uid);
      const trainerDocSnap = await getDoc(trainerDocRef);
      if (!trainerDocSnap.exists()) {
        toast.error('Trainer does not exist.');
        setSubmitting(false);
        return;
      }
      const trainerData = trainerDocSnap.data();

      // Generate unique report ID (custom human-readable ID)
      const reportId = await generateUniqueReportId('OSR');

      // Prepare the report data
      const reportData = {
        ...formData,
        reportId,
        createdBy: currentUser.uid,
        createdAt: Timestamp.now(),
        reportDate: Timestamp.fromDate(reportDate),
        traineeName: traineeData.name || 'Unknown',
        traineeIdNumber: traineeData.idNumber || '',
        trainerName: trainerData.name || 'Unknown',
        trainerIdNumber: trainerData.idNumber || '',
        traineeId: formData.traineeId,
        trainerId: currentUser.uid,
        archived: traineeArchived,
      };

      // Save to Firestore
      const docRef = await addDoc(collection(db, 'officerSafetyReports'), reportData);
      
      // Create notification for the trainee - with high priority for safety reports
      await createReportNotification(
        formData.traineeId,
        traineeData.name || 'Unknown',
        trainerData.name || 'Unknown',
        'OSR',
        reportId,    // customReportId
        docRef.id,   // firestoreDocId
        reportDate
      );
      
      // If submission is successful and we had a draft, delete it
      if (draftId) {
        try {
          const draftRef = doc(db, 'osrDrafts', draftId);
          await deleteDoc(draftRef);
          setDraftId(null);
        } catch (error) {
          console.error('Error deleting draft after submission:', error);
        }
      }
      
      toast.success(
        `Officer Safety Report created successfully! Report ID: ${reportId}`
      );
      navigate('/dors');
    } catch (error) {
      console.error('Error creating Officer Safety Report:', error);
      toast.error('Failed to create the report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Helper to get current year-month (YY-MM)
  const getCurrentYearMonth = () => {
    const now = new Date();
    const year = now.getFullYear().toString().slice(-2);
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    return `${year}-${month}`;
  };

  // Helper to generate a unique ID
  const generateUniqueReportId = async (reportType) => {
    const yearMonth = getCurrentYearMonth(); // e.g. '24-11'
    const counterDocId = `${reportType}-${yearMonth}`;
    const countersRef = doc(db, 'counters', counterDocId);
    let reportId = '';

    await runTransaction(db, async (transaction) => {
      const countersDoc = await transaction.get(countersRef);

      let lastNumber = 1;
      if (!countersDoc.exists()) {
        transaction.set(countersRef, { lastNumber });
      } else {
        lastNumber = countersDoc.data().lastNumber + 1;
        transaction.update(countersRef, { lastNumber });
      }

      const lastNumberStr = lastNumber.toString().padStart(5, '0');
      reportId = `${yearMonth}-${reportType}-${lastNumberStr}`;
    });

    return reportId;
  };

  // Input field styling matching CreateDOR.js
  const inputStyle = `
    w-full 
    p-3 
    rounded-md 
    bg-secondary-light/30 
    dark:bg-secondary-dark 
    border 
    border-secondary-light 
    dark:border-secondary-dark 
    focus:ring-2 
    focus:ring-accent-light 
    dark:focus:ring-accent-dark 
    focus:border-transparent 
    outline-none 
    transition-all 
    duration-200
    text-text-light
    dark:text-white
  `;

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="p-4 sm:p-6 md:p-8 bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark transition-colors duration-300"
      >
        <div className="max-w-4xl mx-auto">
          {/* Header with back button */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
          >
            <button 
              onClick={() => navigate('/dors')}
              className="inline-flex items-center text-sm font-medium text-accent-light dark:text-accent-dark hover:text-accent-dark dark:hover:text-accent-light transition-colors duration-200"
            >
              <FiArrowLeft className="mr-2" />
              Back to Reports
            </button>
            
            <div className="flex items-center gap-2 sm:gap-4">
              {!loadingDraft && (
                <>
                  <button
                    onClick={handleSaveDraft}
                    disabled={savingDraft}
                    className="inline-flex items-center text-xs sm:text-sm font-medium text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 transition-colors duration-200"
                  >
                    <FiSave className="mr-1 sm:mr-2" />
                    <span className="whitespace-nowrap">
                      {savingDraft ? 'Saving...' : (draftId ? 'Update Draft' : 'Save Draft')}
                    </span>
                  </button>
                  
                  {draftId && (
                    <button
                      onClick={handleDeleteDraft}
                      disabled={submitting || savingDraft}
                      className="inline-flex items-center text-xs sm:text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors duration-200"
                    >
                      <FiTrash2 className="mr-1 sm:mr-2" />
                      <span className="whitespace-nowrap">Delete Draft</span>
                    </button>
                  )}
                </>
              )}
              
              <button
                onClick={() => setShowHelpText(!showHelpText)}
                className="inline-flex items-center text-xs sm:text-sm font-medium text-accent-light dark:text-accent-dark hover:text-accent-dark dark:hover:text-accent-light transition-colors duration-200"
              >
                <FiHelpCircle className="mr-1 sm:mr-2" />
                <span className="whitespace-nowrap">{showHelpText ? 'Hide Help' : 'Show Help'}</span>
              </button>
            </div>
          </motion.div>

          {/* Help text */}
          <AnimatePresence>
            {showHelpText && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
            <div className="mb-6 p-4 bg-secondary-light/30 dark:bg-secondary-dark/30 rounded-lg border border-secondary-light dark:border-secondary-dark">
              <h3 className="flex items-center text-lg font-semibold mb-2">
                <FiInfo className="mr-2 text-accent-light dark:text-accent-dark" />
                About Officer Safety Reports
              </h3>
              <p className="text-sm md:text-base">
                Officer Safety Reports (OSRs) document incidents where a trainee's safety was compromised or where safety protocols were violated.
                These reports help identify training needs and prevent future safety issues.
              </p>
              <ul className="mt-3 list-disc list-inside text-sm md:text-base space-y-1">
                <li>Select the date when the incident occurred</li>
                <li>Choose the trainee involved in the incident</li>
                <li>Provide a detailed description of what happened</li>
                <li>Document specific safety concerns and any remedial actions taken</li>
              </ul>
              <p className="mt-3 text-sm md:text-base font-medium">
                Note: Officer Safety Reports should be submitted as soon as possible after an incident occurs.
              </p>
            </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Main form card */}
          <motion.div 
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.4, ease: "easeOut" }}
            className="bg-primary-light dark:bg-primary-dark rounded-lg shadow-lg border border-secondary-light dark:border-secondary-dark overflow-hidden transition-all duration-300"
          >
            {/* Card header */}
            <div className="p-4 sm:p-6 border-b border-secondary-light dark:border-secondary-dark bg-secondary-light/10 dark:bg-secondary-dark/10">
              <h1 className="text-xl sm:text-2xl font-bold flex items-center">
                <FiAlertTriangle className="mr-3 text-accent-light dark:text-accent-dark" />
                {draftId ? 'Edit Draft Officer Safety Report' : 'Create Officer Safety Report'}
              </h1>
              {draftId && (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-text-light/70 dark:text-text-dark/70 mt-2">
                    <FiEdit className="inline mr-1" /> You are working on a saved draft
                  </p>
                </div>
              )}
            </div>

            {/* Form content */}
            <div className="p-4 sm:p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Date and Trainee Selection */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="block font-medium flex items-center text-sm">
                      <FiCalendar className="mr-2 text-accent-light dark:text-accent-dark" />
                      Incident Date
                    </label>
                    <input
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      className={inputStyle}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block font-medium flex items-center text-sm">
                      <FiUser className="mr-2 text-accent-light dark:text-accent-dark" />
                      Select Trainee
                    </label>
                    <select
                      name="traineeId"
                      value={formData.traineeId}
                      onChange={handleInputChange}
                      className={inputStyle}
                      required
                    >
                      <option value="">Select a trainee</option>
                      {trainees.map((trainee) => (
                        <option key={trainee.id} value={trainee.id}>
                          {trainee.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Incident Description */}
                <div className="space-y-2">
                  <label className="block font-medium flex items-center text-sm">
                    <FiAlertTriangle className="mr-2 text-accent-light dark:text-accent-dark" />
                    Incident Description
                  </label>
                  <textarea
                    name="incidentDescription"
                    value={formData.incidentDescription}
                    onChange={handleInputChange}
                    className={`${inputStyle} h-32`}
                    rows="5"
                    placeholder="Provide a detailed description of the safety incident or violation..."
                    required
                  />
                </div>

                {/* Safety Concerns */}
                <div className="space-y-2">
                  <label className="block font-medium flex items-center text-sm">
                    <FiClipboard className="mr-2 text-accent-light dark:text-accent-dark" />
                    Safety Concerns & Remedial Actions
                  </label>
                  <textarea
                    name="safetyConcerns"
                    value={formData.safetyConcerns}
                    onChange={handleInputChange}
                    className={`${inputStyle} h-32`}
                    rows="5"
                    placeholder="Describe specific safety concerns and any remedial actions taken..."
                    required
                  />
                </div>

                {/* Submit Button */}
                <div className="p-4 sm:p-6 border-t border-secondary-light dark:border-secondary-dark bg-secondary-light/5 dark:bg-secondary-dark/5 flex flex-wrap justify-end gap-3">
                  {availableDrafts.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setIsDraftSelectorModalOpen(true)}
                      className="flex-grow sm:flex-grow-0 sm:w-auto px-4 py-2 rounded-md flex items-center justify-center font-medium
                        bg-blue-500/90 dark:bg-blue-600/90
                        text-white
                        hover:bg-blue-600 dark:hover:bg-blue-700
                        focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 dark:focus:ring-blue-600
                        transition-all duration-200 text-sm"
                    >
                      <FiList className="mr-1.5" />
                      Switch Draft
                    </button>
                  )}
                  
                  <button
                    type="submit"
                    className="flex-grow sm:flex-grow-0 sm:w-auto px-5 py-2 rounded-md flex items-center justify-center font-medium
                      bg-accent-light dark:bg-accent-dark
                      text-primary-light dark:text-primary-dark
                      hover:brightness-110
                      focus:ring-2 focus:ring-offset-1 focus:ring-accent-light dark:focus:ring-accent-dark
                      disabled:opacity-70 disabled:cursor-not-allowed
                      transition-all duration-200 text-sm"
                    disabled={submitting || savingDraft}
                  >
                    <FiCheckCircle className="mr-1.5" />
                    {submitting ? 'Submitting...' : 'Submit Report'}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        ref={modalRef}
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleConfirm}
        title="Confirm Report Creation"
        message="Are you sure you want to create this Officer Safety Report?"
        confirmText="Create Report"
        cancelText="Cancel"
      />

      {/* Draft Modal */}
      <ConfirmationModal
        isOpen={isDraftModalOpen}
        onClose={() => {
          startFresh();
        }}
        onConfirm={() => {
          loadDraft(draftId, true);
          setIsDraftModalOpen(false);
        }}
        title="Draft Found"
        message="You have a saved draft for an Officer Safety Report. Would you like to load it and continue editing?"
        confirmText="Load Draft"
        cancelText="Start Fresh"
      />

      {/* Draft Selector Modal */}
      {isDraftSelectorModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/70">
          <div className="bg-primary-light dark:bg-primary-dark p-6 rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-y-auto border border-secondary-light dark:border-secondary-dark">
            <h2 className="text-xl font-bold mb-4 text-text-light dark:text-text-dark">
              Multiple Drafts Found
            </h2>
            <p className="mb-4 text-text-light/70 dark:text-text-dark/70 text-sm">
              You have multiple saved drafts for different trainees. Please select one to edit or start fresh.
            </p>
            
            <div className="space-y-3 mb-4">
              {availableDrafts.map(draft => {
                const trainee = trainees.find(t => t.id === draft.traineeId);
                const traineeName = trainee ? trainee.name : 'Unknown Trainee';
                
                let lastUpdated = 'Unknown date';
                if (draft.lastUpdated && draft.lastUpdated.toDate) {
                  const date = draft.lastUpdated.toDate();
                  lastUpdated = date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
                }
                
                return (
                  <div 
                    key={draft.id} 
                    className="border border-secondary-light dark:border-secondary-dark rounded-lg p-3 
                      hover:bg-secondary-light/10 dark:hover:bg-secondary-dark/10 cursor-pointer
                      transition-colors duration-200
                      bg-secondary-light/5 dark:bg-secondary-dark/5"
                    onClick={() => selectDraft(draft.id)}
                  >
                    <div className="font-semibold text-text-light dark:text-text-dark">
                      {traineeName}
                    </div>
                    <div className="text-sm text-text-light/70 dark:text-text-dark/70">
                      Last updated: {lastUpdated}
                    </div>
                    <div className="text-xs text-text-light/50 dark:text-text-dark/50 mt-1">
                      Draft ID: {draft.id.substring(0, 8)}...
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="flex justify-between gap-3">
              <button
                className="px-4 py-2 rounded-md
                  bg-secondary-light dark:bg-secondary-dark
                  text-text-light dark:text-text-dark
                  hover:bg-secondary-light/80 dark:hover:bg-secondary-dark/80
                  transition-colors duration-200"
                onClick={startFresh}
              >
                Start Fresh
              </button>
              <button
                className="px-4 py-2 rounded-md
                  bg-accent-light dark:bg-accent-dark
                  text-primary-light dark:text-primary-dark
                  hover:brightness-110
                  transition-all duration-200"
                onClick={() => setIsDraftSelectorModalOpen(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Draft Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteDraftModalOpen}
        onClose={() => setIsDeleteDraftModalOpen(false)}
        onConfirm={confirmDeleteDraft}
        title="Confirm Delete Draft"
        message="Are you sure you want to delete this draft? This action cannot be undone."
        confirmText="Delete Draft"
        cancelText="Cancel"
        confirmButtonColor="red"
      />
    </>
  );
};

export default CreateOfficerSafetyReport;
